import { useCallback, useState } from "react"
import { GridActionsCellItem, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid"
import { useEndpointProvider } from "../../../Providers/EndpointProvider"
import { useModalProvider } from "../../../Providers/ModalProvider"
import CreateSiteModals from "./Subcomponents/CreateSiteModals"
import ModifySiteModals from "./Subcomponents/ModifySiteModal"
import ModernMUITable from "../../../Components/ModernMUITable"
import EyeIcon from "../../../Components/Icons/EyeIcon"
import { MdOutlineModeEdit } from "react-icons/md"
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal"
import { useAuthProvider } from "../../../Providers/AuthProvider"
import { permissionTooltip } from "../../../Utils/permissionUtils"


export default function Sites() {
	const { createModal, close } = useModalProvider()
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const queryClient = useQueryClient()
	const authProvider = useAuthProvider()
	const canManageSites = authProvider.permissions?.['site:manage']

	// Row selection state for delete functionality
	const [selectedSiteIds, setSelectedSiteIds] = useState([])

	const deleteDisabledReason = permissionTooltip(canManageSites)
		|| (selectedSiteIds.length === 0 ? "Select sites to delete." : "")

	// Pagination state
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })

	function renderSiteModal() {
		createModal(<CreateSiteModals style={{ height: "80%" }} close={close} escClose={false} cli ckOutsideClose={false} />)
	}

	const handleClick = useCallback((row, viewMode) => {
		createModal(<ModifySiteModals style={{ height: "80%" }} close={close} row={row} viewMode={viewMode} escClose={false} clickOutsideClose={false} />)
	}, [close, createModal])

	// Delete sites mutation
	const deleteMutation = useMutation({
		mutationFn: async (site_ids) => {
			return endpointProvider.post(LINKS.ADMIN.SITES.DELETE_SITES, { site_ids })
		},
		onSuccess: () => {
			setSelectedSiteIds([])
			queryClient.invalidateQueries(["sites"])
		}
	})

	const handleDeleteSites = () => {
		if (selectedSiteIds.length > 0) {
			createModal(<ConfirmationModal
				title="Delete sites?"
				description={`Are you sure you want to permanently delete ${selectedSiteIds.length} site${selectedSiteIds.length > 1 ? 's' : ''}? This action cannot be undone. Note: Sites with patients cannot be selected for deletion.`}
				confirmButton={{
					text: "Yes, delete",
					theme: MobergTheme.RED,
					onClick: () => deleteMutation.mutate(selectedSiteIds)
				}}
			/>)
		}
	}

	const handleSelectionChange = (selectionArray) => {
		setSelectedSiteIds(selectionArray)
	}

	// Function to determine if a row can be selected (sites with patients cannot be deleted)
	const isRowSelectable = (params) => {
		return canManageSites && (params.row.patient_count === 0 || params.row.patient_count === null || params.row.patient_count === undefined)
	}

	const columns = [
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			hideable: false,
		},
		{ field: "identifier", flex: 0.4, headerName: "Site ID", minWidth: 100, visible: true },
		{ field: "name", flex: 1, headerName: "Site name", minWidth: 120, visible: true },
		{ field: "description", flex: 1, headerName: "Description", minWidth: 110, visible: true },
		{ field: "location", flex: 1, headerName: "Location", minWidth: 120, visible: true },
		{ field: "user_count", flex: 0.4, headerName: "No. users", valueGetter: (value, row) => row?.user_count, minWidth: 100, visible: true },
		{ field: "patient_count", flex: 0.4, headerName: "No. patients", valueGetter: (value, row) => row?.patient_count, minWidth: 120, visible: true },
		{
			field: "actions",
			flex: 0.4,
			headerName: "Actions",
			type: "actions",
			hideable: false,
			getActions: params => [
				<GridActionsCellItem icon={<EyeIcon />} label="View" onClick={() => handleClick(params.row, true)} showInMenu />,
				<GridActionsCellItem icon={<MdOutlineModeEdit size={20} />} label="Edit" onClick={() => handleClick(params.row)} showInMenu disabled={!canManageSites} />,
			],
			visible: true,
			minWidth: 120
		},
	]

	const siteTableToolbarProps = {
		searchInput: { isShow: true },
		columnButton: { isShow: true },
		mainTable: true
	}

	const queryProps = {
		queryKey: "sites",
		endpoint: LINKS.ADMIN.SITES.GET_SITES,
	}

	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

	return (
		<div style={{ 
			position: 'relative', 
			padding: '10px 30px 20px', 
			height: 'calc(100vh - 200px)',
			minHeight: '500px',
			maxHeight: '80vh'
		}}>
			{/* Action buttons above table */}
			<div style={{ paddingBottom: '10px', display: 'flex', gap: '12px', alignItems: 'center' }}>
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.BLUE}
					shape={MobergButtonShape.RECTANGLE}
					onClick={renderSiteModal}
					disabled={!canManageSites}
					tooltip={permissionTooltip(canManageSites)}
				>
					Create site
				</MobergButton>
				
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.RED}
					shape={MobergButtonShape.RECTANGLE}
					onClick={handleDeleteSites}
					disabled={!!deleteDisabledReason}
					tooltip={deleteDisabledReason}
				>
					Delete ({selectedSiteIds.length})
				</MobergButton>
			</div>

			<ModernMUITable 
				columns={columns} 
				tableToolbarProps={siteTableToolbarProps} 
				{...queryProps} 
				paginationMode="server"
				sortingMode="server"
			paginationModel={paginationModel}
			onPaginationModelChange={setPaginationModel}
			supportsPageSize={true}
				height="100%"
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={setColumnVisibilityModel}
				enableRowSelection={true}
				onSelectionChange={handleSelectionChange}
				isRowSelectable={isRowSelectable}
				persistStateInUrl={true}
				tableId="sites"
				defaultSortModel={[
					{ field: 'identifier', sort: 'asc' }
				]}
			/>
		</div>
	)
}